/**
 * Typed wrappers around our Cloud Functions callables.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

interface SetupDeviceArgs {
  /** The 8-char Crockford-base32 setup code printed on the button's QR
   * sticker (pre-flashed units) or read off serial (BYO) — e.g. `XDVKQ5K7`.
   * The AP SSID is a generic "RemindMeVoice-Setup" and no longer carries the
   * code. The server resolves the code to the device ID via setup_code_index. */
  setupCode: string;
  accountId: string;
  roomLabel?: string;
}
interface SetupDeviceResult {
  ok: boolean;
  deviceId: string;
  accountId: string;
}

interface DeviceSetupStatusArgs {
  /** Same setup code as SetupDeviceArgs.setupCode. */
  setupCode: string;
}
interface DeviceSetupStatusResult {
  /** True once the device has Wi-Fi + has registered (TOFU). Until then,
   * setup_device would fail — the wizard polls this before claiming. */
  registered: boolean;
  /** Explicit live-connection flag: true once the button is heartbeating to us,
   * cleared when we deliver its clearWifi (Unlink) directive or at unlink. The
   * wizard claims only when connected — a re-claim keeps the code `registered`
   * even while the button sits offline in captive portal, so `registered` alone
   * is insufficient. */
  connected: boolean;
}

interface FirmwareManifestResult {
  version: string;
  /** Public URL of the current factory bin, for the in-browser BYO flasher. */
  factoryUrl: string;
  factoryMd5?: string | null;
  factorySizeBytes?: number | null;
}

interface UnlinkDeviceArgs {
  deviceId: string;
}
interface UnlinkDeviceResult {
  ok: boolean;
  deviceId: string;
}

interface CreateAccountArgs {
  /** The new elder's display name — shown across the app and in the elder
   * switcher. Trimmed + validated server-side (1–100 chars). */
  elderName: string;
  /** Must be true — the caregiver ticked the HealthConsentCheckbox. The
   * server rejects the call otherwise and stamps healthConsentAt/By/Version
   * on the new account doc. */
  healthConsent: boolean;
}
interface CreateAccountResult {
  ok: boolean;
  /** The new account's id — navigate to /accounts/:accountId/... after. */
  accountId: string;
}

interface CreateInviteArgs {
  accountId: string;
  email: string;
  role: 'caregiver' | 'admin';
}
interface CreateInviteResult {
  token: string;
  inviteId: string;
}

interface AcceptInviteArgs {
  token: string;
}
interface AcceptInviteResult {
  ok: boolean;
  accountId: string;
}

interface GetInvitePreviewArgs {
  token: string;
}
export interface GetInvitePreviewResult {
  role: 'admin' | 'caregiver';
  status: 'pending' | 'accepted' | 'revoked' | 'expired' | 'invalid';
  /** The elder this invite is to help care for, for the page heading. */
  elderName: string;
  /** Redacted invited address, e.g. `m***@gmail.com` — display only; the raw
   * address is never sent to the client. */
  emailRedacted: string;
  /** Whether the signed-in caller's email matches the invited address
   * (computed server-side). Gates the Accept button. */
  emailMatches: boolean;
  /** Whether the caller's email is verified. `accept_invite` hard-requires
   * this, so an unverified-but-matching invitee is prompted to verify. */
  emailVerified: boolean;
}

interface RevokeInviteArgs {
  inviteId: string;
}

interface ResendInviteArgs {
  inviteId: string;
}

interface ListInvitesArgs {
  accountId: string;
}
export interface PendingInvite {
  inviteId: string;
  email: string;
  role: 'admin' | 'caregiver';
  status: 'pending' | 'expired';
  createdAt: string | null;
  expiresAt: string | null;
}
interface ListInvitesResult {
  invites: PendingInvite[];
}

import type { Phrases } from '@/schemas';

interface PreviewSummaryArgs {
  accountId: string;
  /** ISO-8601 with offset, e.g. 2026-05-01T14:30:00+01:00. */
  atIso: string;
  withAudio: boolean;
  phrasesOverride?: Phrases;
  voiceOverride?: string;
  speakingRateOverride?: number;
  pitchOverride?: number;
  /** Draft morning-nudge toggle + wake hour, so the server can decide whether
   * the morning nudge is active at `atIso` (and emit it in `activeNotifications`)
   * from the caregiver's unsaved settings, not just the saved ones. */
  morningNudgeEnabled?: boolean;
  wakeHour?: number;
  /** Draft 24-hour-wording toggle, so the preview text reflects the unsaved
   * setting (like phrasesOverride reflects unsaved phrase edits). */
  use24HourTime?: boolean;
  /** When set, the server derives the active reminder phrase itself ("event" =
   * the reminder firing at `atIso`; "morning" = the morning-nudge prompt),
   * synthesises it at the (override) voice settings, and returns it as
   * `notificationAudioUrl`. Lets "Listen to the prompt" track the live sliders
   * like the summary preview — without the server synthesising client text.
   * Pass `phrasesOverride` alongside so unsaved prompt drafts are reflected. */
  notificationPreview?: 'event' | 'morning';
  /** Freshness floor for the server's materialised event cache, in epoch ms —
   * the newest event state this client has SEEN (max event updatedAt, plus a
   * wall-clock stamp when a snapshot change moves no updatedAt, e.g. a
   * delete). If the cache was built before this, the server renders from
   * live event reads instead, so a just-edited event previews correctly
   * instead of racing the async cache rebuild. */
  eventsSyncedAtMs?: number;
}

interface PreviewActiveNotification {
  eventTitle: string;
  minutesBefore: number;
  /** ISO-8601 with offset — instant the reminder is configured to fire. */
  fireAt: string;
  /** The exact phrase the device would speak. Empty string means the
   * account-wide `reminderPrompt` template is empty (silent
   * reminder — LED only). */
  spokenText: string;
  /** Per-event canonical audio URL. Present when the template embeds
   * event variables; null otherwise (account-level URL applies). */
  audioUrl: string | null;
  /** Which reminder this is — `'event'` (calendar reminder) or `'morning'`
   * (the morning nudge). Drives the "Listen to the prompt" synth request. */
  kind: 'event' | 'morning';
}

interface PreviewSummaryResult {
  text: string;
  /** Greeting clip URL — present iff withAudio=true. Played first. The
   * SAME content-addressed blob the device's prefix request uses. */
  greetingUrl?: string;
  /** Body (summary) clip URL — present iff withAudio=true. Played after
   * the greeting. The app plays the two in sequence, mirroring the device
   * (greeting clip then body clip) so the preview is byte-faithful. */
  bodyUrl?: string;
  /** Notification-prompt clip URL — present iff `notificationPreview` was passed
   * and the server derived a non-empty phrase. Synthesised at the (override)
   * voice settings so the "Listen to the prompt" preview matches what the button
   * would say at the current sliders. */
  notificationAudioUrl?: string;
  /** Reminders configured to be active at `atIso`. Always present (may
   * be an empty array). Drives the Voice page annotation block. */
  activeNotifications: PreviewActiveNotification[];
}

interface RemoveMemberArgs {
  accountId: string;
  uid: string;
}

interface ChangeMemberRoleArgs {
  accountId: string;
  uid: string;
  role: 'admin' | 'caregiver';
}

interface LeaveAccountArgs {
  accountId: string;
}

interface DeleteAccountArgs {
  accountId: string;
}

interface CreateHardwareCheckoutArgs {
  accountId: string;
  /** 1–5 (matches MAX_BUTTONS_PER_ACCOUNT in functions/main.py). */
  quantity: number;
}
interface CreateHardwareCheckoutResult {
  /** Hosted Stripe Checkout URL to redirect the browser to. */
  url: string;
}

interface CreateSubscriptionCheckoutArgs {
  accountId: string;
}
interface CreateSubscriptionCheckoutResult {
  /** Hosted Stripe Checkout (subscription mode) URL to redirect to. */
  url: string;
}

interface CreateBillingPortalArgs {
  accountId: string;
}
interface CreateBillingPortalResult {
  /** Stripe customer-portal URL to redirect the browser to. */
  url: string;
}

export const setupDevice = httpsCallable<SetupDeviceArgs, SetupDeviceResult>(functions, 'setup_device');
export const deviceSetupStatus = httpsCallable<DeviceSetupStatusArgs, DeviceSetupStatusResult>(functions, 'device_setup_status');
export const firmwareManifest = httpsCallable<Record<string, never>, FirmwareManifestResult>(functions, 'firmware_manifest');
export const previewSummary = httpsCallable<PreviewSummaryArgs, PreviewSummaryResult>(functions, 'preview_summary');
export const unlinkDevice = httpsCallable<UnlinkDeviceArgs, UnlinkDeviceResult>(functions, 'unlink_device');
export const createAccount = httpsCallable<CreateAccountArgs, CreateAccountResult>(functions, 'create_account');
export const createInvite = httpsCallable<CreateInviteArgs, CreateInviteResult>(functions, 'create_invite');
export const acceptInvite = httpsCallable<AcceptInviteArgs, AcceptInviteResult>(functions, 'accept_invite');
export const getInvitePreview = httpsCallable<GetInvitePreviewArgs, GetInvitePreviewResult>(functions, 'get_invite_preview');
export const revokeInvite = httpsCallable<RevokeInviteArgs, { ok: boolean }>(functions, 'revoke_invite');
export const resendInvite = httpsCallable<ResendInviteArgs, { ok: boolean }>(functions, 'resend_invite');
export const listInvites = httpsCallable<ListInvitesArgs, ListInvitesResult>(functions, 'list_invites');
export const removeMember = httpsCallable<RemoveMemberArgs, { ok: boolean }>(functions, 'remove_member');
export const changeMemberRole = httpsCallable<ChangeMemberRoleArgs, { ok: boolean; noop?: boolean }>(functions, 'change_member_role');
export const leaveAccount = httpsCallable<LeaveAccountArgs, { ok: boolean }>(functions, 'leave_account');
export const deleteAccount = httpsCallable<DeleteAccountArgs, { ok: boolean }>(functions, 'delete_account');
export const createHardwareCheckout = httpsCallable<CreateHardwareCheckoutArgs, CreateHardwareCheckoutResult>(functions, 'create_hardware_checkout');
export const createSubscriptionCheckout = httpsCallable<CreateSubscriptionCheckoutArgs, CreateSubscriptionCheckoutResult>(functions, 'create_subscription_checkout');
export const createBillingPortalSession = httpsCallable<CreateBillingPortalArgs, CreateBillingPortalResult>(functions, 'create_billing_portal_session');

/** One turn of the in-app assistant conversation. Mirrors the server's
 * validated shape; history is held client-side (nothing is persisted). */
export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}
interface AssistantChatArgs {
  accountId: string;
  messages: AssistantMessage[];
  /** Optional short tag naming the screen the widget is on (e.g. 'device-setup').
   * The server maps it to a system-prompt hint; unknown/absent → no effect. */
  context?: string;
}
interface AssistantChatResult {
  reply: string;
}
export const assistantChat = httpsCallable<AssistantChatArgs, AssistantChatResult>(functions, 'assistant_chat');

interface GetDeviceStatusArgs {
  accountId: string;
}
export interface DeviceStatus {
  deviceId: string;
  /** ISO-8601 UTC of the device's last heartbeat, or null if never seen. */
  lastSeen: string | null;
}
interface GetDeviceStatusResult {
  /** Minutes of silence after which a button is treated as offline — the
   * same threshold the device-down alert email uses, so the Settings dot
   * and the email agree. */
  thresholdMinutes: number;
  devices: DeviceStatus[];
}
export const getDeviceStatus = httpsCallable<GetDeviceStatusArgs, GetDeviceStatusResult>(functions, 'get_device_status');
